import React, { useState } from 'react';
import { ShieldIcon, NetworkIcon, LightbulbIcon, CheckCircleIcon } from 'lucide-react';
import { FadeIn } from '../components/FadeIn';
import { SectionHeading } from '../components/SectionHeading';
import { AccordionItem } from '../components/AccordionItem';
import { PageHero } from '../components/PageHero';
import { ProcessTimeline } from '../components/ProcessTimeline';
import { Button } from '../components/Button';
interface WoonbotenPageProps {
  onNavigate: (page: string) => void;
}
export function WoonbotenPage({
  onNavigate
}: WoonbotenPageProps) {
  const [openAccordion, setOpenAccordion] = useState<number | null>(0);
  const faqs = [{
    question: 'Welke banken financieren woonboten?',
    answer: 'Niet elke reguliere bank financiert woonboten, omdat het risicoprofiel anders is dan bij een stenen huis. Ik heb een specifiek netwerk van gespecialiseerde geldverstrekkers die hier wél ervaring mee hebben. Ik weet precies waar ik jouw aanvraag met de grootste kans op succes kan indienen.'
  }, {
    question: 'Wat zijn de specifieke eisen voor een woonboothypotheek?',
    answer: 'Er gelden vaak aanvullende eisen. Zo moet de woonboot een vaste ligplaats hebben met een vergunning, ingeschreven staan in het scheepsregister van het Kadaster, en er is vaak een recent taxatierapport van een gespecialiseerde woonboottaxateur nodig. Ik begeleid je bij al deze specifieke stappen.'
  }, {
    question: 'Hoe verschilt een woonboothypotheek van een reguliere hypotheek?',
    answer: 'De rentetarieven liggen vaak iets hoger door een kleine toeslag, en het maximale leenbedrag ten opzichte van de woningwaarde kan afwijken. Ook de notariële afhandeling is specifieker. Omdat ik deze niche door en door ken, kom je niet voor verrassingen te staan.'
  }, {
    question: 'Wat kost een woonboothypotheek?',
    answer: 'De rentetarieven voor woonboten liggen gemiddeld 0,5% tot 1,5% hoger dan bij een reguliere hypotheek, afhankelijk van de geldverstrekker en het type woonboot. Tijdens een vrijblijvend gesprek bereken ik precies wat de maandlasten in jouw situatie worden.'
  }, {
    question: 'Kan ik een woonboot kopen in Amsterdam?',
    answer: 'Zeker. Amsterdam heeft veel woonboten, maar de ligplaatsvergunningen zijn schaars en waardevol. Ik ken de specifieke regels per stadsdeel en weet welke geldverstrekkers ervaring hebben met Amsterdamse woonboten. Of het nu gaat om de Amstel, de Prinsengracht of de Schinkel — ik help je verder.'
  }, {
    question: 'Heb ik een taxatie nodig voor mijn woonboot?',
    answer: 'Ja, je hebt een taxatierapport nodig van een gespecialiseerde woonboottaxateur. Dit is anders dan een reguliere woningtaxatie. De taxateur beoordeelt onder andere de staat van de romp, de ligplaats en de vergunningssituatie. Ik kan je doorverwijzen naar betrouwbare woonboottaxateurs in de regio.'
  }];
  const timelineSteps = [{
    number: 1,
    title: 'Haalbaarheid',
    description: 'Ik check direct de haalbaarheid op basis van de specifieke woonboot, ligplaats en jouw financiële situatie.'
  }, {
    number: 2,
    title: 'Dossier opbouwen',
    description: 'Ik verzamel samen met jou de juiste documenten, zoals een gespecialiseerd taxatierapport en bewijs van inschrijving in het scheepsregister.'
  }, {
    number: 3,
    title: 'Financiering rond',
    description: 'Ik dien de aanvraag in bij de juiste bank en begeleid het traject tot aan de notaris.'
  }];
  return <div className="w-full" data-id="element-809">
      {/* Hero */}
      <PageHero variant="default" title="Dé Specialist in Woonboot Hypotheken" subtitle="De meeste banken zeggen 'nee' tegen woonboten. Ik heb het netwerk en de kennis om jouw financiering wél rond te krijgen." label="Woonboten" data-id="element-810" />

      {/* Content Block */}
      <section className="py-20 md:py-28 bg-white" data-id="element-811">
        <div className="max-w-3xl mx-auto px-4 md:px-8 text-center" data-id="element-812">
          <SectionHeading title="Een unieke woning vraagt om unieke kennis" subtitle="Het financieren van een woonboot is een vak apart. Reguliere banken haken vaak af of stellen onmogelijke eisen. Ik heb de ervaring, de specifieke kennis en het juiste netwerk om de financiering van jouw droom op het water te regelen." data-id="element-813" />
          <FadeIn delay={0.2} data-id="element-814">
            <p className="text-lg text-gray-600 leading-relaxed" data-id="element-815">
              Of het nu gaat om een aankoop, verbouwing of het oversluiten van
              een bestaande woonboothypotheek: ik neem de complexiteit uit
              handen. Ik ken de specifieke eisen rondom ligplaatsvergunningen,
              de officiële registratie bij het Kadaster en taxaties.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Waarom JDG voor woonboten? */}
      <section className="py-20 md:py-28 bg-warm" data-id="element-816">
        <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-817">
          <SectionHeading title="Waarom JDG voor je woonboot?" data-id="element-818" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-819">
            <FadeIn delay={0.1} className="bg-white p-8 rounded-2xl shadow-card border border-gray-100" data-id="element-820">
              <div className="w-14 h-14 bg-navy/5 rounded-full flex items-center justify-center mb-6" data-id="element-821">
                <NetworkIcon className="w-7 h-7 text-gold" data-id="element-822" />
              </div>
              <h3 className="text-xl font-bold text-navy mb-3" data-id="element-823">
                Specialist Netwerk
              </h3>
              <p className="text-gray-600" data-id="element-824">
                Ik werk samen met de selecte groep banken die wél woonboten
                financieren. Ik weet precies waar jouw aanvraag de grootste kans
                van slagen heeft.
              </p>
            </FadeIn>

            <FadeIn delay={0.2} className="bg-white p-8 rounded-2xl shadow-card border border-gray-100" data-id="element-825">
              <div className="w-14 h-14 bg-navy/5 rounded-full flex items-center justify-center mb-6" data-id="element-826">
                <CheckCircleIcon className="w-7 h-7 text-gold" data-id="element-827" />
              </div>
              <h3 className="text-xl font-bold text-navy mb-3" data-id="element-828">
                Jarenlange Ervaring
              </h3>
              <p className="text-gray-600" data-id="element-829">
                Van ligplaatsvergunningen tot scheepsregisters: ik ken de
                valkuilen en begeleid je veilig door het specifieke juridische
                en financiële proces.
              </p>
            </FadeIn>

            <FadeIn delay={0.3} className="bg-white p-8 rounded-2xl shadow-card border border-gray-100" data-id="element-830">
              <div className="w-14 h-14 bg-navy/5 rounded-full flex items-center justify-center mb-6" data-id="element-831">
                <LightbulbIcon className="w-7 h-7 text-gold" data-id="element-832" />
              </div>
              <h3 className="text-xl font-bold text-navy mb-3" data-id="element-833">
                Creatieve Oplossingen
              </h3>
              <p className="text-gray-600" data-id="element-834">
                Lijkt de financiering vast te lopen? Ik denk in oplossingen en
                zoek net zolang door tot we de juiste constructie hebben
                gevonden.
              </p>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Wat maakt woonbootfinanciering zo anders? */}
      <section className="py-20 md:py-28 bg-white" data-id="element-835">
        <div className="max-w-4xl mx-auto px-4 md:px-8" data-id="element-836">
          <SectionHeading title="Wat maakt woonbootfinanciering zo anders?" data-id="element-837" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-838">
            <FadeIn delay={0.1} className="bg-warm p-8 rounded-2xl border border-gray-100 shadow-card relative" data-id="element-839">
              <div className="w-10 h-10 bg-gold rounded-full flex items-center justify-center text-white font-bold shadow-md mb-4" data-id="element-840">
                1
              </div>
              <h3 className="text-xl font-bold text-navy mb-3" data-id="element-841">
                Niet elke bank doet mee
              </h3>
              <p className="text-gray-600" data-id="element-842">
                De meeste reguliere banken financieren geen woonboten. Het
                risicoprofiel is anders dan bij een stenen huis. Ik werk samen
                met een selecte groep gespecialiseerde geldverstrekkers die hier
                wél ervaring mee hebben — en ik weet precies bij welke bank jouw
                aanvraag de grootste kans van slagen heeft.
              </p>
            </FadeIn>

            <FadeIn delay={0.2} className="bg-warm p-8 rounded-2xl border border-gray-100 shadow-card relative" data-id="element-843">
              <div className="w-10 h-10 bg-gold rounded-full flex items-center justify-center text-white font-bold shadow-md mb-4" data-id="element-844">
                2
              </div>
              <h3 className="text-xl font-bold text-navy mb-3" data-id="element-845">
                Andere spelregels
              </h3>
              <p className="text-gray-600" data-id="element-846">
                Bij een woonboothypotheek gelden andere regels. De rente ligt
                vaak iets hoger door een kleine toeslag op de rente, het
                maximale leenbedrag ten opzichte van de woningwaarde kan
                afwijken, en je hebt een gespecialiseerd taxatierapport nodig.
                Daarnaast moet je woonboot ingeschreven staan in het
                scheepsregister van het Kadaster en heb je een geldige
                ligplaatsvergunning nodig.
              </p>
            </FadeIn>

            <FadeIn delay={0.3} className="bg-warm p-8 rounded-2xl border border-gray-100 shadow-card relative" data-id="element-847">
              <div className="w-10 h-10 bg-gold rounded-full flex items-center justify-center text-white font-bold shadow-md mb-4" data-id="element-848">
                3
              </div>
              <h3 className="text-xl font-bold text-navy mb-3" data-id="element-849">
                Verzekering is maatwerk
              </h3>
              <p className="text-gray-600" data-id="element-850">
                Een woonboot verzekeren is niet hetzelfde als een huis
                verzekeren. Je hebt een specifieke opstal- en
                inboedelverzekering nodig die rekening houdt met risico's op het
                water. Ik heb de juiste connecties om dit goed en betaalbaar te
                regelen.
              </p>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Process Timeline */}
      <ProcessTimeline title="Het proces in 3 stappen" steps={timelineSteps} data-id="element-851" />

      {/* Insurance Cross-sell */}
      <section className="py-12 md:py-16 bg-navy text-white" data-id="element-852">
        <div className="max-w-4xl mx-auto px-4 md:px-8" data-id="element-853">
          <FadeIn className="flex flex-col md:flex-row items-center gap-8 text-center md:text-left" data-id="element-854">
            <div className="w-14 h-14 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-855">
              <ShieldIcon className="w-7 h-7 text-gold" data-id="element-856" />
            </div>
            <div data-id="element-857">
              <h3 className="text-2xl font-bold text-gold mb-3" data-id="element-858">
                Vergeet de woonbootverzekering niet
              </h3>
              <p className="text-gray-300 leading-relaxed mb-6" data-id="element-859">
                Naast de financiering is ook het verzekeren van een woonboot
                maatwerk. Ik heb de juiste connecties voor een passende opstal-
                en inboedelverzekering voor jouw schip.
              </p>
              <Button variant="ghost" onClick={() => onNavigate('verzekeringen')} className="text-white hover:text-gold" data-id="element-860">
                Bekijk mijn verzekeringen
              </Button>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="py-20 md:py-28 bg-white" data-id="element-861">
        <div className="max-w-3xl mx-auto px-4 md:px-8" data-id="element-862">
          <SectionHeading title="Veelgestelde Vragen" subtitle="Alles wat je moet weten over het financieren van een woonboot." data-id="element-863" />

          <FadeIn delay={0.2} className="bg-warm rounded-2xl shadow-card p-6 md:p-10 border border-gray-100" data-id="element-864">
            {faqs.map((faq, index) => <AccordionItem key={index} question={faq.question} answer={faq.answer} isOpen={openAccordion === index} onClick={() => setOpenAccordion(openAccordion === index ? null : index)} data-id="element-865" />)}
          </FadeIn>

          <FadeIn delay={0.4} className="mt-16 text-center" data-id="element-866">
            <p className="text-navy font-medium mb-6" data-id="element-867">
              Staat jouw vraag er niet tussen?
            </p>
            <Button variant="primary" size="lg" onClick={() => onNavigate('contact')} data-id="element-868">
              Plan een gratis kennismaking
            </Button>
          </FadeIn>
        </div>
      </section>

      {/* Cross-page CTA */}
      <section className="py-12 md:py-16 bg-warm" data-id="element-869">
        <div className="max-w-3xl mx-auto px-4 md:px-8 text-center" data-id="element-870">
          <FadeIn data-id="element-871">
            <p className="text-gray-500 mb-3" data-id="element-872">
              Liever een reguliere hypotheek?
            </p>
            <Button variant="ghost" onClick={() => onNavigate('hypotheken')} data-id="element-873">
              Bekijk mijn hypotheekadvies
            </Button>
          </FadeIn>
        </div>
      </section>
    </div>;
}